<h4>What this screen is for</h4>
<p>
    When several business rules of the same type match the same journey, only one
    can win. FareVantage normally decides by how specific each rule is. This screen
    lets you overrule that for a chosen set of journeys.
</p>

<h4>How the automatic order works</h4>
<p>
    Without an override, the most specific match wins, in this order:
</p>
<ol class="priority-list">
    <li>Service + both stations</li>
    <li>Service + one <code>*ALL</code></li>
    <li>Service + <code>*ALL</code>/<code>*ALL</code></li>
    <li>Market + both stations</li>
    <li>Market + one <code>*ALL</code></li>
    <li>Market + <code>*ALL</code>/<code>*ALL</code></li>
    <li>No service + both stations</li>
    <li>No service + one <code>*ALL</code></li>
    <li>No service + <code>*ALL</code>/<code>*ALL</code></li>
</ol>

<h4>Getting here from Inventory</h4>
<p>
    A red dot in the <strong>BR</strong> column of the Inventory workbench means a rule
    that matched that journey did not take effect. Its rule detail lists what lost and
    what beat it, and its <strong>Re-prioritize</strong> button opens this screen with
    the service, journey, class, rule type and date range already filled in from that
    journey &mdash; so you can go straight to Search.
</p>

<h4>Finding the rules</h4>
<p>
    Set the filter and search. You must choose a <strong>Class</strong> and a
    <strong>Rule Type</strong>, and at least one <strong>Market</strong> or
    <strong>Service</strong>. The summary line shows what you searched for and how
    many journeys matched; <strong>View Journeys</strong> lists them and lets you
    export the list.
</p>
<p>
    Each row in <strong>Rule Priorities</strong> is a rule that matches at least one
    of those journeys, with the priority it currently carries. Click a rule to see
    its full definition &mdash; dates, days of week, bucket rules and journey rules.
</p>

<h4>Changing the order</h4>
<p>
    Drag the rows into the order you want, then <strong>Save</strong>. You will be
    asked for the dates the override should be active, and for the journeys it
    covers &mdash; origin, destination and services. These default to what you
    searched for, and <code>*ALL</code> is used where you searched broadly.
</p>

<h4>Worth knowing</h4>
<ul>
    <li>
        <strong>Saving resets the priority of every rule in that scope, not just the ones
        you moved.</strong> Any override previously saved for those journeys is replaced.
    </li>
    <li>
        <strong>Using <code>*ALL</code> is wider than it looks.</strong> An override saved
        against <code>*ALL</code> stations clears overrides previously saved for
        individual stations underneath it, and a market clears overrides for the
        services in it.
    </li>
    <li>
        <strong>Overrides expire.</strong> Once the end date has passed the override is
        removed permanently, and the automatic order takes over again.
    </li>
    <li>
        <strong>Dragging can only hold or raise a rule's number, never lower it.</strong>
        The numbers are recalculated from the order, so the position in the list and the
        number shown may not agree.
    </li>
    <li>
        The class saved with the override comes from the <strong>Class</strong> filter,
        not from the save dialog.
    </li>
    <li>
        If more than 75 rules match you will be asked to narrow the search, and the list
        is cleared. Broad searches can also time out.
    </li>
    <li>
        The journey count counts each rule match separately, so one journey matched by
        three rules counts three times.
    </li>
</ul>
